// Purchase plumbing — mirrors use-waitlist-submit's shape. Sales are "open"
// only when the Worker has full Stripe+Keygen config (GET /api/sales), so the
// pricing section flips from waitlist CTAs to Buy buttons with zero code changes
// at go-live. Checkout failures fall back to the waitlist modal at the call site.
import { useEffect, useState } from 'react';
import { getAttribution, track } from '../track';

export type PurchasePlan = 'monthly' | 'annual' | 'founding';

export function useSalesOpen(): boolean {
  // Sales are live and permanent, so default OPEN — this makes the CTAs read
  // "Buy" on first paint and in previews without a Worker (e.g. `npm run dev`,
  // where /api/sales 404s). We only downgrade to waitlist copy if the Worker
  // EXPLICITLY reports sales closed. A failed checkout still falls back to the
  // request-access modal at the call site, so nobody hits a dead button.
  const [open, setOpen] = useState(true);
  useEffect(() => {
    let alive = true;
    fetch('/api/sales')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && d.open === false) setOpen(false); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return open;
}

// Rewardful's tracking script (index.html) exposes the visitor's referral UUID
// once loaded; absent (script blocked / no referral) we simply omit it.
declare global {
  interface Window { Rewardful?: { referral?: string } }
}

// Starts Stripe Checkout; resolves false (instead of navigating) on any failure.
export async function startCheckout(plan: PurchasePlan): Promise<boolean> {
  try {
    const referral = typeof window.Rewardful?.referral === 'string' ? window.Rewardful.referral : '';
    // First-touch attribution rides into Stripe metadata → Keygen license
    // metadata, so every sale knows its channel.
    const attr = getAttribution();
    const r = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, ...(referral ? { referral } : {}), ...(attr ? { attr } : {}) }),
    });
    if (!r.ok) return false;
    const d = (await r.json()) as { url?: string };
    if (!d.url) return false;
    track('checkout_start', plan);
    window.location.assign(d.url);
    return true;
  } catch {
    return false;
  }
}
